import { ChevronDown, Pause, Play, XCircle } from 'lucide-react';
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import type { JobApi } from '@/modules/jobs/types';
import { Button } from '@/modules/ui/components/button/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/modules/ui/components/dropdown-menu/DropdownMenu';

import { CloseJobDialog } from './CloseJobDialog';
import { ToggleSearchDialog } from './ToggleSearchDialog';

// A notch below the menu default (14px text, 16px icons).
const itemClassName = 'text-[13px] [&_svg]:!size-3.5';

interface JobStatusMenuProps {
  job: JobApi;
  searchSuffix: string;
}

/**
 * "Edit job" split button: the chevron pauses, resumes or closes a live job,
 * so the header keeps one control.
 */
export function JobStatusMenu({ job, searchSuffix }: JobStatusMenuProps) {
  const [isToggleOpen, setIsToggleOpen] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const [isCloseOpen, setIsCloseOpen] = useState(false);
  const isPaused = job.state === 'OnHold';
  const chevronRef = useRef<HTMLButtonElement>(null);

  // Keyboard users continue from the chevron after either dialog closes.
  const focusChevron = (event: Event) => {
    event.preventDefault();
    chevronRef.current?.focus();
  };

  return (
    <div className="flex items-center">
      <Button
        asChild
        className="h-8 rounded-md rounded-r-none"
        size="sm"
        variant="outline"
      >
        <Link to={`/hm/jobs/${job.id}/edit${searchSuffix}`}>Edit job</Link>
      </Button>
      {/* Non-modal: a modal menu that hands off to a dialog leaves
          `pointer-events: none` on <body>, freezing the page. */}
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            aria-label="Change job status"
            className="-ml-px h-8 rounded-md rounded-l-none px-2"
            ref={chevronRef}
            size="sm"
            variant="outline"
          >
            <ChevronDown />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            className={itemClassName}
            onSelect={() => {
              setIsResuming(isPaused);
              setIsToggleOpen(true);
            }}
          >
            {isPaused ? <Play /> : <Pause />}
            {isPaused ? 'Resume search…' : 'Pause search…'}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className={itemClassName}
            onSelect={() => setIsCloseOpen(true)}
            variant="destructive"
          >
            <XCircle /> Close job…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <ToggleSearchDialog
        job={job}
        onCloseAutoFocus={focusChevron}
        onOpenChange={setIsToggleOpen}
        open={isToggleOpen}
        resume={isResuming}
      />
      <CloseJobDialog
        job={job}
        onCloseAutoFocus={focusChevron}
        onOpenChange={setIsCloseOpen}
        open={isCloseOpen}
      />
    </div>
  );
}
